/**
 * Formats a number as a currency string in USD format.
 *
 * @param value - The numeric value to format
 * @returns A formatted currency string (e.g., "$1,234.56")
 *
 * @example
 * ```typescript
 * formatCurrency(1234.56); // Returns "$1,234.56"
 * formatCurrency(-42.99);  // Returns "-$42.99"
 * ```
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

/**
 * Formats a decimal number as a percentage string with sign.
 *
 * @param value - The decimal value to format (e.g., 0.15 for 15%)
 * @returns A formatted percentage string with + or - prefix (e.g., "+15.00%")
 *
 * @example
 * ```typescript
 * formatPercent(0.1542);  // Returns "+15.42%"
 * formatPercent(-0.0823); // Returns "-8.23%"
 * formatPercent(0);       // Returns "+0.00%"
 * ```
 */
export const formatPercent = (value: number): string => {
  const formatted = (value * 100).toFixed(2);
  return `${value >= 0 ? '+' : ''}${formatted}%`;
};

/**
 * Formats a number with thousands separators.
 *
 * @param value - The numeric value to format
 * @returns A formatted number string with commas (e.g., "1,234,567")
 *
 * @example
 * ```typescript
 * formatNumber(1234567);   // Returns "1,234,567"
 * formatNumber(42);        // Returns "42"
 * formatNumber(1234567.89); // Returns "1,234,567.89"
 * ```
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Formats a market capitalization value with appropriate suffix (T, B, M).
 *
 * @param value - The market cap value in dollars
 * @returns A formatted string with appropriate suffix (e.g., "$1.23T", "$45.67B", "$890.12M")
 *
 * @example
 * ```typescript
 * formatMarketCap(1500000000000); // Returns "$1.50T"
 * formatMarketCap(45670000000);   // Returns "$45.67B"
 * formatMarketCap(890120000);     // Returns "$890.12M"
 * formatMarketCap(50000);         // Returns "$50,000.00"
 * ```
 */
export const formatMarketCap = (value: number): string => {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return formatCurrency(value);
};

/**
 * Formats a volume value with appropriate suffix (B, M, K).
 *
 * @param volume - The volume value
 * @returns A formatted string with appropriate suffix (e.g., "1.23B", "45.67M", "890.12K")
 *
 * @example
 * ```typescript
 * formatVolume(1500000000); // Returns "1.50B"
 * formatVolume(45670000);   // Returns "45.67M"
 * formatVolume(890120);     // Returns "890.12K"
 * formatVolume(500);        // Returns "500"
 * ```
 */
export const formatVolume = (volume: number): string => {
  if (volume >= 1e9) return `${(volume / 1e9).toFixed(2)}B`;
  if (volume >= 1e6) return `${(volume / 1e6).toFixed(2)}M`;
  if (volume >= 1e3) return `${(volume / 1e3).toFixed(2)}K`;
  return volume.toString();
};
